import { useState, useRef, useEffect } from "react"
import ReactHtmlParser from "react-html-parser"
import Svg from "../_svgs/svg"

export default function LostBaggageGuideComponent({ componentData, airlines }) {
  const [activeStep, setActiveStep] = useState(1)
  const [selectedHandlingPolicy, setselectedHandlingPolicy] = useState(null)
  const [selectedCarrier, setSelectedCarrier] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")

  // refs for stepper steps
  const stepRefs = [useRef(null), useRef(null), useRef(null)]
  const containerRef = useRef(null) // Add a ref for the root container

  const stepLabels = [1, 2, 3]

  // filter carriers by search term
  const filteredCarriers = airlines.filter((carrier) => carrier.name.toLowerCase().includes(searchTerm.toLowerCase()))

  const stepThreeVerificationCheck = () => {
    return selectedHandlingPolicy.policyActive
  }

  const handleSelectHandlingPolicy = (policy) => {
    setselectedHandlingPolicy(policy)
    if (policy.selectAirline) {
      setActiveStep(2)
      setSelectedCarrier(null)
      setSearchTerm("")
    } else {
      setActiveStep(2)
    }
  }

  const handleSelectCarrier = (carrier) => {
    setSelectedCarrier(carrier)
    setActiveStep(3)
  }

  // focus management for accessibility reasons
  useEffect(() => {
    let hasMounted = LostBaggageGuideComponent._hasMounted
    if (!hasMounted) {
      LostBaggageGuideComponent._hasMounted = true
      return
    }
    let visibleStepIndex = activeStep - 1
    const showNoHandlingPolicy = activeStep === 2 && selectedCarrier && stepThreeVerificationCheck()
    if (visibleStepIndex === 2 && showNoHandlingPolicy) {
      visibleStepIndex = 1
    }
    if (stepRefs[visibleStepIndex] && stepRefs[visibleStepIndex].current) {
      stepRefs[visibleStepIndex].current.focus()
    }
  }, [activeStep, selectedCarrier, selectedHandlingPolicy])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        setActiveStep(1)
        setselectedHandlingPolicy(null)
        setSelectedCarrier(null)
        setSearchTerm("")
      }
    }
    const node = containerRef.current
    if (node) {
      node.addEventListener("keydown", handleKeyDown)
    }
    return () => {
      if (node) {
        node.removeEventListener("keydown", handleKeyDown)
      }
    }
  }, [])

  const handlePreviousStep = () => {
    if (activeStep === 2 || (activeStep === 3 && !selectedHandlingPolicy.selectAirline)) {
      setActiveStep(1)
      setselectedHandlingPolicy(null)
      setSelectedCarrier(null)
      setSearchTerm("")
    } else if (activeStep === 3) {
      setActiveStep(2)
      setSelectedCarrier(null)
    }
  }

  const handleReset = () => {
    setActiveStep(1)
    setselectedHandlingPolicy(null)
    setSelectedCarrier(null)
    setSearchTerm("")
  }

  // determines if selected carrier has a matching handling policy
  const showNoHandlingPolicy = activeStep === 2 && selectedHandlingPolicy.selectAirline === false

  // step descriptions
  const stepDescriptions = [
    ReactHtmlParser(componentData.stepOneDescription),
    ReactHtmlParser(componentData.stepTwoDescription),
    componentData &&
      componentData.stepThreeDescription &&
      ReactHtmlParser(
        componentData.stepThreeDescription.formatUnicorn({ handling: `<strong>${selectedCarrier?.handlingCompany?.name}</strong>` || "" }),
      ),
  ]

  return (
    <div className="lost-baggage-guide" ref={containerRef} tabIndex={-1}>
      <div className="lost-baggage-guide__header-container">
        <div className="lost-baggage-guide__header">
          <h2>{componentData.heading}</h2>
          <h3>{componentData.subHeader}</h3>
        </div>
      </div>

      <div className="lost-baggage-guide__content">
        <div className="lost-baggage-guide__steps-container">
          {/* stepper */}
          <ul className="lost-baggage-guide__stepper">
            {stepLabels.map((step, idx) => {
              const isHidden = step === 3 && showNoHandlingPolicy
              return (
                <li
                  key={step}
                  className={"lost-baggage-guide__step" + (activeStep >= step ? " active" : "") + (isHidden ? " hidden" : "")}
                  tabIndex={activeStep === step && !isHidden ? 0 : -1}
                  ref={stepRefs[idx]}
                  aria-current={activeStep === step && !isHidden ? `step ${step}` : undefined}
                >
                  {step}
                </li>
              )
            })}
          </ul>

          {/* steps */}
          {!showNoHandlingPolicy && (
            <div className="rich-text lost-baggage-guide__step-description">
              {activeStep === 1 && stepDescriptions[0]}
              {activeStep === 2 && stepDescriptions[1]}
              {activeStep === 3 && stepDescriptions[2]}
            </div>
          )}

          {/* step 1 */}
          {activeStep === 1 && (
            <div className="lost-baggage-guide__handling-policies">
              {componentData.handlingLostAndFoundPolicyList?.map((policy, idx) => (
                <button
                  key={policy.id}
                  className={"handling-policy-btn" + (selectedHandlingPolicy?.id === policy.id ? " selected" : "")}
                  onClick={() => handleSelectHandlingPolicy(policy)}
                >
                  {policy.policyLogo ? (
                    <img tabIndex={-1} src={policy.policyLogo} alt="" aria-hidden="true" className="handling-logo" />
                  ) : policy.policyIcon ? (
                    <Svg
                      isDecorative={true}
                      iconName={policy && policy.policyIcon && policy.policyIcon[0] && policy.policyIcon[0].value}
                      className="indicator"
                    />
                  ) : undefined}
                  <span className="handling-policy-btn__text">{policy.policyText}</span>
                  <Svg isDecorative={true} iconClass={"icon arrowright"} iconName="arrowright-slim" className="arrow-icon" />
                </button>
              ))}
            </div>
          )}

          {/* Step 2 */}
          {activeStep === 2 && (
            <div className="lost-baggage-guide__airline-selection">
              {!showNoHandlingPolicy && (
                <>
                  <div className="search-button">
                    <div className="search-icon"></div>
                  </div>
                  <input
                    type="text"
                    placeholder={componentData.stepTwoSearchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="airline-search-input"
                  />
                  <div className="airlines-list">
                    {filteredCarriers.map((carrier) => (
                      <button
                        key={carrier.id}
                        className={"airline-btn" + (selectedCarrier?.id === carrier.id ? " selected" : "")}
                        onClick={() => handleSelectCarrier(carrier)}
                      >
                        <div className="airline-image-container">
                          <img className="airline-image" src={carrier.logo} aria-hidden="true" tabIndex={-1}></img>
                        </div>
                        <span className="airline-separator"></span>
                        <div className="airline-description">
                          <span className="airline-name">{carrier.name}</span>
                          <span className="terminal-text">{carrier.terminal}</span>
                        </div>
                        <Svg isDecorative={true} iconClass={"icon arrowright"} iconName="arrowright-slim" className="arrow-icon" />
                      </button>
                    ))}
                  </div>
                </>
              )}
              {showNoHandlingPolicy && (
                <div className="rich-text no-handling-policy">{ReactHtmlParser(selectedHandlingPolicy.contactInformation)}</div>
              )}
            </div>
          )}
          {/* Step 3 */}
          {activeStep === 3 && selectedCarrier && selectedCarrier.handlingCompany && (
            <div className="lost-baggage-guide__step-three">
              <div className="contact-instructions">
                <div className="contact-header">
                  <h2>{selectedCarrier.handlingCompany.name}</h2>
                  {selectedCarrier.handlingCompany.logo ? (
                    <>
                      <span className="handling-separator"></span>
                      <img className="handling-logo" src={selectedCarrier.handlingCompany.logo} alt="" aria-hidden="true" tabIndex={-1}></img>
                    </>
                  ) : null}
                </div>
                {selectedCarrier.handlingCompany.phone && (
                  <div className="contact-detail">
                    <Svg isDecorative={true} iconName={selectedCarrier.handlingCompany.phoneIcon} className="indicator" />
                    <div className="contact-phone-details">
                      <h3>{selectedCarrier.handlingCompany.phoneLabel}</h3>
                      <div className="rich-text">{ReactHtmlParser(selectedCarrier.handlingCompany.phone)}</div>
                    </div>
                  </div>
                )}
                {selectedCarrier.handlingCompany.lostBagsEmail && (
                  <div className="contact-detail">
                    <Svg isDecorative={true} iconName={selectedCarrier.handlingCompany.lostBagsEmailIcon} className="indicator" />
                    <div className="contact-email-details">
                      <h3>{selectedCarrier.handlingCompany.lostBagsEmailLabel}</h3>
                      <div className="rich-text">{ReactHtmlParser(selectedCarrier.handlingCompany.lostBagsEmail)}</div>
                    </div>
                  </div>
                )}
                {selectedCarrier.handlingCompany.address && (
                  <div className="contact-detail">
                    <Svg isDecorative={true} iconName={selectedCarrier.handlingCompany.addressIcon} className="indicator" />
                    <div className="contact-address-details">
                      <h3>{selectedCarrier.handlingCompany.addressLabel}</h3>
                      <div className="rich-text">{ReactHtmlParser(selectedCarrier.handlingCompany.address)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeStep === 3 && !selectedCarrier && (
            <div className="lost-baggage-guide__step-three">
              <div className="contact-instructions">
                <span className="rich-text">{ReactHtmlParser(selectedHandlingPolicy.contactInformation)}</span>
              </div>
            </div>
          )}

          {/* back tracking / reset */}
          <div className="lost-baggage-guide__controls">
            {(activeStep === 2 || activeStep === 3) && (
              <button className={activeStep === 2 ? "back-btn l-push" : "back-btn"} onClick={handlePreviousStep}>
                {componentData.previousStepLabel}
              </button>
            )}
            {activeStep === 3 && (
              <button className="start-again-btn" onClick={handleReset}>
                {componentData.startAgainLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
